import { ThunkDispatch } from "core-integration/src/store";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import {
  getPlayerData,
  getPlayerUpdateError,
} from "core-integration/src/store/player/reducers";
import {
  getValidatedAccounts,
  otpSendEmailVerify,
  otpValidateEmailVerify,
  playerStitch,
} from "core-integration/src/store/player/thunks";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass";
import styled from "styled-components";
import { IPlayer } from "../../types";
import Alert from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import EntryHistoryTable from "../EntryHistoryTable";
import { FieldFeedback, InputField, RadioGroup } from "../FieldRenderers";
import { ArrowRightLong } from "../icons/Arrows";
import { Main, Wrapper } from "../Layout";
import Panel from "../Panel";
import Title from "../Title";

interface IAccount {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface ISeasonHistory {
  id: number;
  player_id: number;
  season_name: string;
  total_points: number;
  rank: number;
}

interface IState {
  email: string;
  errorEmail: string;
  otpToken: string;
  submittedToken: string;
  otpSent: boolean;
  otpVerified: boolean;
  errorOtp: string;
  accounts: IAccount[];
  seasonHistory: ISeasonHistory[];
  selectedAccountId: number | null;
  stitchSuccess: boolean;
  showVerifySection: boolean;
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.space[2]};
  align-items: flex-start;
  margin: ${(props) => `${props.theme.space[4]} 0 0`};
`;

const TitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  margin: ${(props) =>
    `0 ${props.theme.space[2]} ${props.theme.space[2]} ${props.theme.space[2]}`};
`;

const FieldWrap = styled.div`
  align-self: stretch;
`;

const ButtonWrap = styled.div`
  margin: ${(props) => `${props.theme.space[2]} 0`};
`;

const AlertWrap = styled.div`
  align-self: stretch;
`;

const StyledArrowRightLong = styled(ArrowRightLong)`
  vertical-align: middle;
`;

interface AccountCardProps {
  account: IAccount;
  history: ISeasonHistory[] | undefined;
  noHistoryText: string;
}

const AccountCard = ({ account, history, noHistoryText }: AccountCardProps) => {
  return (
    <>
      <p>
        <strong>
          {t(
            "player.accounts.seasonHistoryFor",
            "Season history for {{name}} ({{email}})",
            {
              name: `${account.first_name} ${account.last_name}`,
              email: account.email,
            },
          )}
        </strong>
      </p>
      {history ? (
        <EntryHistoryTable seasonHistory={history} />
      ) : (
        <p>{noHistoryText}</p>
      )}
    </>
  );
};

type Props = WithTranslation;

const Accounts = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const player = useSelector(getPlayerData) as IPlayer | null;
  const playerUpdateError = useSelector(getPlayerUpdateError);

  const verifyEmailRef = useRef<HTMLDivElement>(null);
  const linkAccountsRef = useRef<HTMLDivElement>(null);
  const scrollIntent = useRef<"verify" | "link" | null>(null);

  const [state, setState] = useState<IState>({
    email: "",
    errorEmail: "",
    otpToken: "",
    submittedToken: "",
    otpSent: false,
    otpVerified: false,
    errorOtp: "",
    accounts: [],
    seasonHistory: [],
    selectedAccountId: null,
    stitchSuccess: false,
    showVerifySection: true,
  });

  const noHistoryText = t(
    "player.accounts.noSeasonHistory",
    "No season history available for this account.",
  );

  const errorsTxt = {
    EEmail: t("register.errors.email", "An email is required"),
    EOtp: t("register.errors.otp", "Code is required"),
    EAccount: t("player.accounts.error.account", "Please select an account"),
  };

  const groupedHistory = state.seasonHistory.reduce(
    (acc, h) => {
      if (!acc[h.player_id]) acc[h.player_id] = [];
      acc[h.player_id].push(h);
      return acc;
    },
    {} as Record<number, ISeasonHistory[]>,
  );

  const fetchValidatedAccounts = async () => {
    try {
      const data = await dispatch(getValidatedAccounts());
      setState((state) => {
        const firstOther: IAccount | undefined = data.accounts.find(
          (a: IAccount) => a.id !== player?.id,
        );
        return {
          ...state,
          accounts: data.accounts,
          seasonHistory: data.season_history,
          ...(!state.selectedAccountId &&
            firstOther && { selectedAccountId: firstOther.id }),
        };
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchValidatedAccounts();
  }, []);

  useEffect(() => {
    if (playerUpdateError?.badRequest?.email?.[0]?.code === "no_otp_email") {
      setState((state) => ({
        ...state,
        otpSent: true,
        errorOtp: "",
        errorEmail: "",
      }));
    }
  }, [playerUpdateError]);

  const handleEmailChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setState((state) => ({ ...state, email: value, errorEmail: "" }));
  };

  const handleOtpTokenChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setState((state) => ({ ...state, otpToken: value, errorOtp: "" }));
  };

  const handleSendOtp = async () => {
    if (!state.email) {
      setState((state) => ({ ...state, errorEmail: errorsTxt.EEmail }));
      return;
    }
    try {
      await dispatch(otpSendEmailVerify({ email: state.email }));
      setState((state) => ({
        ...state,
        otpSent: true,
        errorOtp: "",
        errorEmail: "",
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleVerifyOtp = async () => {
    if (!state.otpToken) {
      setState((state) => ({ ...state, errorOtp: errorsTxt.EOtp }));
      return;
    }
    setState((state) => ({ ...state, submittedToken: state.otpToken }));
    try {
      await dispatch(
        otpValidateEmailVerify({ email: state.email, token: state.otpToken }),
      );
      setState((state) => ({
        ...state,
        otpVerified: true,
        otpSent: false,
        errorOtp: "",
        otpToken: "",
        submittedToken: "",
        showVerifySection: false,
      }));
      fetchValidatedAccounts();
    } catch (e) {
      console.error(e);
    }
  };

  const handleAccountChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setState((state) => ({ ...state, selectedAccountId: parseInt(value) }));
  };

  const handleStitch = async () => {
    if (!state.selectedAccountId) {
      setState((state) => ({ ...state, errorOtp: errorsTxt.EAccount }));
      return;
    }
    try {
      await dispatch(playerStitch({ player_id: state.selectedAccountId }));
      await dispatch(bootstrap());
      setState((state) => ({ ...state, stitchSuccess: true }));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (scrollIntent.current === "verify") {
      verifyEmailRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (scrollIntent.current === "link") {
      linkAccountsRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    scrollIntent.current = null;
  }, [state.showVerifySection]);

  const handleVerifyAnother = () => {
    scrollIntent.current = "verify";
    setState((state) => ({
      ...state,
      email: "",
      errorEmail: "",
      errorOtp: "",
      otpToken: "",
      otpSent: false,
      otpVerified: false,
      stitchSuccess: false,
      submittedToken: "",
      showVerifySection: true,
    }));
  };

  const handleShowLinkedAccounts = () => {
    scrollIntent.current = "link";
    setState((state) => ({ ...state, showVerifySection: false }));
  };

  if (!player) return null;
  const otherAccounts = state.accounts.filter((a) => a.id !== player.id);
  const currentAccount =
    state.accounts.find((a) => a.id === player.id) ??
    (player
      ? {
          id: player.id,
          email: player.email,
          first_name: player.first_name,
          last_name: player.last_name,
        }
      : undefined);
  const currentHistory = groupedHistory[player.id];
  const selectedAccount = state.accounts.find(
    (a) => a.id === state.selectedAccountId,
  );
  const selectedHistory = state.selectedAccountId
    ? groupedHistory[state.selectedAccountId]
    : undefined;

  const stitchErrorMessage =
    playerUpdateError?.badRequest?.player_id?.[0]?.message ||
    (typeof playerUpdateError?.badRequest === "object"
      ? playerUpdateError.badRequest.non_field_errors?.[0]?.message
      : undefined);

  const otpErrorMessage =
    playerUpdateError?.badRequest?.token?.[0]?.message ||
    (typeof playerUpdateError?.badRequest === "string"
      ? playerUpdateError.badRequest
      : undefined);

  return (
    <Wrapper>
      <Main>
        <TitleWrap>
          <Title>{t("player.accounts.title", "Link Accounts")}</Title>
          {!state.showVerifySection && !state.stitchSuccess ? (
            <ButtonWrap>
              <Button
                type="button"
                variant="secondary"
                endIcon={<StyledArrowRightLong fill="white" />}
                onClick={handleVerifyAnother}
              >
                {t("player.accounts.verifyAnother", "Verify another account")}
              </Button>
            </ButtonWrap>
          ) : state.accounts.length > 0 && !state.stitchSuccess ? (
            <ButtonWrap>
              <Button
                type="button"
                variant="secondary"
                endIcon={<StyledArrowRightLong fill="white" />}
                onClick={handleShowLinkedAccounts}
              >
                {t("player.accounts.showVerified", "Show verified accounts")}
              </Button>
            </ButtonWrap>
          ) : null}{" "}
        </TitleWrap>

        {stitchErrorMessage && (
          <AlertWrap>
            <Alert type="error">{stitchErrorMessage}</Alert>
          </AlertWrap>
        )}
        <Box my={4}>
          <Copy>
            <div>
              <p>
                {t(
                  "player.accounts.description.p1",
                  "Link your accounts to access your past season history. Verify your email, then select the account you'd like to connect.",
                )}
              </p>
              <p>
                {t(
                  "player.accounts.description.p2",
                  "If multiple Fantasy accounts are linked to the same email address, you can switch to a previous account. After verifying your email, you'll see a list of available accounts with identifying details, such as season history, to help you choose. Once you switch, your manager history, leagues, and past seasons will be available. Your team created for the current season will remain unchanged.",
                )}
              </p>
            </div>
          </Copy>
        </Box>
        <Box mx={2}>
          {/* Current Account Panel */}
          {!state.stitchSuccess && currentAccount && (
            <Box my={3}>
              <Panel>
                <Panel.Header
                  title={t("player.accounts.currentAccount", "Current Account")}
                />
                <Panel.Body>
                  <Box p={3} pt={1}>
                    <p>
                      Your profile is currently linked to the following account
                      and season data:
                    </p>
                    <AccountCard
                      account={currentAccount}
                      history={currentHistory}
                      noHistoryText={noHistoryText}
                    />
                  </Box>
                </Panel.Body>
              </Panel>
            </Box>
          )}

          {/* Verify Email Section */}
          {state.showVerifySection && (
            <Box my={3} ref={verifyEmailRef}>
              <Panel>
                <Panel.Header
                  title={t(
                    "player.accounts.verifyTitle",
                    "Verify a different account",
                  )}
                />
                <Panel.Body>
                  <Box p={3} pt={1}>
                    <p>
                      {t(
                        "player.accounts.verifyEmail.p1",
                        "To verify a different account, enter your email address and submit the code sent to your email address.",
                      )}
                    </p>
                    <p>
                      <strong>
                        {t(
                          "register.otp.explain2",
                          `Please note that if the email address is linked to a TV 2 account, you won't be able to stitch it.`,
                        )}
                      </strong>
                    </p>
                    <StyledForm>
                      <FieldWrap>
                        <InputField
                          required
                          id="ismPlayerEmail"
                          type="email"
                          hasErrors={!!state.errorEmail}
                          label={t(
                            "player.accounts.label.email",
                            "Enter email",
                          )}
                          value={state.email}
                          onChange={handleEmailChange}
                          disabled={state.otpSent || state.otpVerified}
                        />
                        {(state.errorEmail ||
                          (playerUpdateError?.badRequest?.email?.[0]?.message &&
                            playerUpdateError.badRequest.email[0].code !==
                              "no_otp_email")) && (
                          <FieldFeedback>
                            {state.errorEmail ||
                              playerUpdateError?.badRequest?.email?.[0]
                                ?.message}
                          </FieldFeedback>
                        )}
                      </FieldWrap>

                      {!state.otpSent && !state.otpVerified && (
                        <ButtonWrap>
                          <Button
                            type="button"
                            onClick={handleSendOtp}
                            disabled={!state.email}
                          >
                            {t("register.label.verifyEmail", "Verify Email")}
                          </Button>
                        </ButtonWrap>
                      )}

                      {state.otpSent && (
                        <>
                          <p>
                            {t(
                              "register.otp.explain",
                              `If the email address is associated with an account, a one-time passcode (OTP) ` +
                                `will be sent from noreply@mailout.fantasy.eliteserien.no. ` +
                                `Please check your inbox and spam folder, then enter the code here to authorise this action.`,
                            )}
                          </p>
                          <FieldWrap>
                            <InputField
                              id="ismRegisterOtp"
                              label={t("register.label.otp", "Enter code")}
                              value={state.otpToken}
                              onChange={handleOtpTokenChange}
                              hasErrors={!!state.errorOtp}
                            />
                            {(state.errorOtp ||
                              (playerUpdateError?.badRequest &&
                                state.otpToken === state.submittedToken)) && (
                              <FieldFeedback>
                                {state.errorOtp || otpErrorMessage}
                              </FieldFeedback>
                            )}
                          </FieldWrap>
                          <ButtonWrap>
                            <Button type="button" onClick={handleVerifyOtp}>
                              {t("register.label.confirmOtp", "Confirm code")}
                            </Button>
                          </ButtonWrap>
                        </>
                      )}

                      {state.otpVerified && (
                        <AlertWrap>
                          <Alert type="success">
                            {t(
                              "register.message.emailVerified",
                              "Email verified successfully!",
                            )}
                          </Alert>
                        </AlertWrap>
                      )}
                    </StyledForm>
                  </Box>
                </Panel.Body>
              </Panel>
            </Box>
          )}

          {/* Link Accounts Panel */}
          {!state.stitchSuccess &&
            state.accounts.length > 0 &&
            !state.showVerifySection && (
              <div ref={linkAccountsRef}>
                <Panel>
                  <Panel.Header
                    title={t(
                      "player.accounts.linkAccountsTitle",
                      "Accounts you can connect",
                    )}
                  />
                  <Panel.Body>
                    <Box p={3} pt={1}>
                      <StyledForm>
                        {otherAccounts.length > 0 && (
                          <FieldWrap>
                            <RadioGroup
                              legend={t(
                                "player.accounts.selectAccount",
                                "The following emails have been verified with a code. Select the account you want to link to:",
                              )}
                              name="selectedAccount"
                              options={otherAccounts.map((a) => ({
                                id: `account-${a.id}`,
                                label: `${a.first_name} ${a.last_name} (${a.email})`,
                                value: a.id.toString(),
                                checked: state.selectedAccountId === a.id,
                                onChange: handleAccountChange,
                              }))}
                            />
                            {stitchErrorMessage && (
                              <FieldFeedback>
                                {stitchErrorMessage}
                              </FieldFeedback>
                            )}
                          </FieldWrap>
                        )}

                        {selectedAccount && (
                          <FieldWrap>
                            <AccountCard
                              account={selectedAccount}
                              history={selectedHistory}
                              noHistoryText={noHistoryText}
                            />
                          </FieldWrap>
                        )}

                        {otherAccounts.length > 0 &&
                          state.selectedAccountId !== player.id && (
                            <ButtonWrap>
                              <Button
                                type="button"
                                onClick={handleStitch}
                                disabled={!state.selectedAccountId}
                              >
                                {t(
                                  "player.accounts.linkAccounts",
                                  "Link Account",
                                )}
                              </Button>
                            </ButtonWrap>
                          )}
                      </StyledForm>
                    </Box>
                  </Panel.Body>
                </Panel>
              </div>
            )}

          {/* No accounts found after verification */}
          {state.otpVerified &&
            !state.stitchSuccess &&
            state.accounts.length === 0 && (
              <p>
                {t(
                  "player.accounts.noAccounts",
                  "No other accounts found for this email address.",
                )}
              </p>
            )}

          {/* Stitch Success */}
          {state.stitchSuccess && selectedAccount && (
            <>
              <AlertWrap>
                <Alert type="success">
                  {t(
                    "player.accounts.stitchSuccess",
                    "Accounts linked successfully!",
                  )}
                </Alert>
              </AlertWrap>
              <FieldWrap>
                <AccountCard
                  account={selectedAccount}
                  history={selectedHistory}
                  noHistoryText={noHistoryText}
                />
              </FieldWrap>
            </>
          )}
        </Box>
      </Main>
    </Wrapper>
  );
};
export default withTranslation()(Accounts);
